import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { isAfter, isBefore, isEqual, parseISO } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { Formik, Form } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import ScheduleDisplay from '@sophocles/components/ScheduleDisplay';
import { selectRelayLoadById } from '@sophocles/entities/RelayLoad/relayLoadSelectors';
import { addToast } from '@sophocles/features/toast';
import { applyApiErrorToFormik } from '@sophocles/utils/apiErrors';
import { formatDateForSubmission } from '@sophocles/utils/formatUtils';

// Form field names, used to attribute API validation errors to their input;
// anything the backend reports outside this list downgrades to the banner.
const CONTRACT_FIELDS = ['contract_start', 'contract_end', 'advertiser', 'override_end_date'];

const parseDateString = (value, originalValue) => {
  if (!originalValue) return null;
  const parsed = originalValue instanceof Date ? originalValue : new Date(originalValue);
  return isNaN(parsed.getTime?.()) ? null : parsed;
};

export default function RelayLoadContractForm({
  loadId,
  contract,
  open,
  onClose,
  onSubmit,
  localTimeZone,
  existingContracts = [],
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [nonFieldMessages, setNonFieldMessages] = React.useState([]);

  React.useEffect(() => {
    if (open) {
      setNonFieldMessages([]);
    }
  }, [open]);

  const todayInTZ = toZonedTime(new Date(), localTimeZone);

  const disablePastDate = date => {
    const formatted = fromZonedTime(`${formatDateForSubmission(date)} 12:00:00`, localTimeZone);
    return isBefore(formatted, todayInTZ);
  };

  const validationSchema = Yup.object({
    contract_start: Yup.date()
      .transform(parseDateString)
      .typeError(t('charting.startDateRequired'))
      .required(t('charting.startDateRequired')),
    contract_end: Yup.date()
      .transform(parseDateString)
      .typeError(t('charting.endDateRequired'))
      .required(t('charting.endDateRequired'))
      .min(Yup.ref('contract_start'), t('charting.endDateAfterStart'))
      .test('no-overlap', t('charting.datesOverlap'), function (end) {
        const { contract_start } = this.parent;
        if (!contract_start || !end) return true;
        return !existingContracts.some(c => {
          const cStart = parseISO(c.contract_start);
          const cEnd = parseISO(c.contract_end);
          return (
            (isBefore(cStart, end) || isEqual(cStart, end)) &&
            (isAfter(cEnd, contract_start) || isEqual(cEnd, contract_start))
          );
        });
      }),
    advertiser: Yup.string().max(50, t('charting.advertiserMaxLength')),
  });

  const isNewType = Boolean(contract?.mapping_name);

  const relayLoad = useSelector(state => selectRelayLoadById(state, loadId));
  const relayLoadName = relayLoad?.get('name', null);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Formik
        enableReinitialize
        initialValues={{
          contract_start: contract?.contract_start
            ? toZonedTime(parseISO(contract.contract_start), Intl.DateTimeFormat().resolvedOptions().timeZone)
            : null,
          contract_end: contract?.contract_end
            ? toZonedTime(parseISO(contract.contract_end), Intl.DateTimeFormat().resolvedOptions().timeZone)
            : null,
          advertiser: contract?.advertiser || '',
          override_end_date: contract?.override_end_date || false,
        }}
        validationSchema={validationSchema}
        validateOnChange
        validateOnBlur
        onSubmit={async (values, { setSubmitting, setErrors }) => {
          setNonFieldMessages([]);
          try {
            const result = await onSubmit({
              ...values,
              contract_start: formatDateForSubmission(values.contract_start),
              contract_end: formatDateForSubmission(values.contract_end),
              relay_load: loadId,
            });
            if (result?.id == null) {
              dispatch(addToast({ message: t('charting.contractCreated'), level: 'success' }));
            } else {
              dispatch(addToast({ message: t('charting.contractUpdated'), level: 'success' }));
            }
            onClose();
          } catch (err) {
            setNonFieldMessages(
              applyApiErrorToFormik(err, {
                setErrors,
                knownFields: CONTRACT_FIELDS,
                genericMessage: t('errors.unexpected', 'Something went wrong. Please try again.'),
              }),
            );
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          dirty,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
          isValid,
        }) => {
          return isNewType ? (
            <>
              <DialogTitle>
                {t('charting.contractDetails')} | {relayLoadName}
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t('charting.advertiser')}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                      {values.advertiser || '-'}
                    </Typography>
                  </Grid>

                  <Grid size={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t('charting.dates')}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                      {formatDateForSubmission(values.contract_start)} - {formatDateForSubmission(values.contract_end)}
                    </Typography>
                  </Grid>

                  <Grid size={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t('charting.description')}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                      {contract.mapping_name}
                    </Typography>
                    <Divider sx={{ my: 1, mt: 2 }} />
                  </Grid>

                  {/* Schedules */}
                  {(contract?.schedules?.length > 0 || contract?.default_schedules?.length > 0) && (
                    <>
                      {/* Contract Schedules Section */}
                      {contract?.schedules?.length > 0 && (
                        <Grid size={12}>
                          <Typography variant="h6" gutterBottom>
                            {t('charting.contractSchedules')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t('charting.contractSchedulesDescription')}
                          </Typography>
                          <Grid container spacing={2}>
                            {contract.schedules.map((schedule, idx) => (
                              <Grid size={{ xs: 12, sm: 6 }} key={`custom-${idx}`}>
                                <ScheduleDisplay schedule={schedule} dimmerAvailable={false} asCard={true} />
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      )}

                      {/* Post-Contract Schedules Section */}
                      {contract?.default_schedules?.length > 0 && (
                        <Grid size={12}>
                          <Typography variant="h6" gutterBottom>
                            {t('charting.postContractSchedules')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t('charting.postContractSchedulesDescription')}
                          </Typography>
                          <Grid container spacing={2}>
                            {contract.default_schedules.map((schedule, idx) => (
                              <Grid size={{ xs: 12, sm: 6 }} key={`default-${idx}`}>
                                <ScheduleDisplay schedule={schedule} dimmerAvailable={false} asCard={true} />
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose}>{t('actions.close', 'Close')}</Button>
              </DialogActions>
            </>
          ) : (
            <Form onSubmit={handleSubmit}>
              <DialogTitle>
                {contract != null ? t('charting.editContract') : t('charting.newContract')} | {relayLoadName}
              </DialogTitle>
              <FormErrorAlert messages={nonFieldMessages} sx={{ mx: 3, mt: 1 }} />
              <DialogContent dividers>
                <Grid container spacing={2}>
                  {/* Advertiser */}
                  <Grid size={12}>
                    <TextField
                      label={t('charting.advertiser')}
                      name="advertiser"
                      fullWidth
                      value={values.advertiser}
                      onChange={handleChange}
                      error={touched.advertiser && Boolean(errors.advertiser)}
                      helperText={touched.advertiser && errors.advertiser}
                    />
                  </Grid>

                  {/* Dates */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DatePicker
                      label={t('charting.startDate')}
                      value={values.contract_start}
                      onOpen={() => setFieldTouched('contract_start')}
                      onChange={value => setFieldValue('contract_start', value)}
                      shouldDisableDate={disablePastDate}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: Boolean(touched.contract_start && errors.contract_start),
                          helperText: touched.contract_start && errors.contract_start,
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DatePicker
                      label={t('charting.endDate')}
                      value={values.contract_end}
                      onOpen={() => setFieldTouched('contract_end')}
                      onChange={value => setFieldValue('contract_end', value)}
                      shouldDisableDate={disablePastDate}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: Boolean(touched.contract_end && errors.contract_end),
                          helperText: touched.contract_end && errors.contract_end,
                        },
                      }}
                    />
                  </Grid>

                  {/* Override End Date */}
                  {!isNewType && contract != null && (
                    <Grid size={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.override_end_date}
                            onChange={e => setFieldValue('override_end_date', e.target.checked)}
                          />
                        }
                        label={t('charting.overrideEndDate')}
                      />
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 2 }}>
                        {t('charting.overrideEndDateDescription')}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose}>{t('actions.cancel', 'Cancel')}</Button>
                <Button type="submit" disabled={isSubmitting || !isValid || !dirty}>
                  {contract != null ? t('actions.update', 'Update') : t('actions.create', 'Create')}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}
